import { z } from "zod";
import {
  isoDateString,
  isoDateTimeString,
  otpCodeSchema,
  phoneSchema,
  uuidSchema,
} from "./common";
import { kycStatusSchema } from "./kyc";

// ============================================================================
// Account identity (UserIdentity / CCCD) + account phone verification — SCRUM-71
// Mirrors the worker KYC shapes (presign → upload → submit → admin review) but
// is per-ACCOUNT and reused across every org the account represents.
// ============================================================================

// Full standardized reject-code set (shared with org KYC / change requests).
export const changeRejectCodeSchema = z.enum([
  "DOC_UNREADABLE",
  "DOC_EXPIRED",
  "NAME_MISMATCH",
  "TAXCODE_MISMATCH",
  "WRONG_DOC_TYPE",
  "REP_ID_MISMATCH",
  "REP_NOT_VERIFIED",
  "DUPLICATE_TAXCODE",
  "OTHER",
]);

export const userIdentityLevelSchema = z.enum([
  "U0_NEW",
  "U1_CONTACT",
  "U2_IDENTITY",
]);

// --- Identity submission (self view) ---
export const userIdentitySelfSchema = z.object({
  status: kycStatusSchema,
  fullName: z.string(),
  idNumberMasked: z.string(),
  dob: isoDateString.nullable().optional(),
  idIssuedAt: isoDateString.nullable().optional(),
  idIssuedPlace: z.string().nullable().optional(),
  idExpiresAt: isoDateString.nullable().optional(),
  rejectCode: changeRejectCodeSchema.nullable().optional(),
  rejectDetail: z.string().nullable().optional(),
  submittedAt: isoDateTimeString,
  reviewedAt: isoDateTimeString.nullable().optional(),
  expiresAt: isoDateTimeString.nullable().optional(),
  // Signed read URLs so the user can review what they submitted before a resubmit.
  idFrontSignedUrl: z.string().url().nullable().optional(),
  idBackSignedUrl: z.string().url().nullable().optional(),
  signedUrlExpiresAt: isoDateTimeString.nullable().optional(),
});

// --- Identity submission (admin view) — decrypted number + guaranteed URLs ---
export const userIdentityAdminSchema = userIdentitySelfSchema.extend({
  userId: uuidSchema,
  userPhone: z.string().nullable().optional(),
  userEmail: z.string().nullable().optional(),
  idNumber: z.string(),
  idFrontSignedUrl: z.string().url(),
  idBackSignedUrl: z.string().url(),
  signedUrlExpiresAt: isoDateTimeString,
});

// --- Admin queue list ---
export const adminIdentityListResponseSchema = z.object({
  items: z.array(userIdentityAdminSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
});

// --- Presign the two CCCD photos ---
const idFileSchema = z.object({
  contentType: z.enum(["image/jpeg", "image/png"]),
  sizeBytes: z
    .number()
    .int()
    .positive()
    .max(10 * 1024 * 1024),
});

export const identityPresignRequestSchema = z.object({
  files: z.object({ idFront: idFileSchema, idBack: idFileSchema }).strict(),
});

export const identityPresignResponseSchema = z.object({
  idFront: z.object({ uploadUrl: z.string().url(), key: z.string() }),
  idBack: z.object({ uploadUrl: z.string().url(), key: z.string() }),
  expiresAt: isoDateTimeString,
});

// --- Submit the identity for review ---
export const identitySubmitRequestSchema = z.object({
  idNumber: z.string().regex(/^[0-9]{9,12}$/, "CCCD 9-12 chữ số"),
  fullName: z.string().min(1).max(120),
  dob: isoDateString.nullable().optional(),
  idIssuedAt: isoDateString.nullable().optional(),
  idIssuedPlace: z.string().max(200).nullable().optional(),
  // If omitted, expiry defaults to submittedAt + 5 years server-side.
  idExpiresAt: isoDateString.nullable().optional(),
  idFrontKey: z.string().min(1),
  idBackKey: z.string().min(1),
  consentAccepted: z.literal(true),
});

export const identitySubmitResponseSchema = z.object({
  submission: userIdentitySelfSchema,
});

export const identityWithdrawResponseSchema = z.object({
  ok: z.literal(true),
});

// --- Admin reject with a standardized code (free-text only for OTHER) ---
export const identityRejectRequestSchema = z
  .object({
    code: changeRejectCodeSchema,
    detail: z.string().max(500).nullable().optional(),
  })
  .refine((v) => v.code !== "OTHER" || (v.detail?.trim().length ?? 0) > 0, {
    message: "detail bắt buộc khi mã là OTHER",
    path: ["detail"],
  });

// --- Phone verification (uses the SCRUM-69 phone OTP infra) ---
export const phoneVerifyStartRequestSchema = z.object({
  phone: phoneSchema,
});

export const phoneVerifyStartResponseSchema = z.object({
  sentAt: isoDateTimeString,
  expiresAt: isoDateTimeString,
});

export const phoneVerifyConfirmRequestSchema = z.object({
  phone: phoneSchema,
  code: otpCodeSchema,
});

export const phoneVerifyConfirmResponseSchema = z.object({
  phoneVerifiedAt: isoDateTimeString,
  identityLevel: userIdentityLevelSchema,
});

export type ChangeRejectCode = z.infer<typeof changeRejectCodeSchema>;
export type UserIdentityLevel = z.infer<typeof userIdentityLevelSchema>;
export type UserIdentitySelf = z.infer<typeof userIdentitySelfSchema>;
export type UserIdentityAdmin = z.infer<typeof userIdentityAdminSchema>;
export type IdentityPresignRequest = z.infer<
  typeof identityPresignRequestSchema
>;
export type IdentityPresignResponse = z.infer<
  typeof identityPresignResponseSchema
>;
export type IdentitySubmitRequest = z.infer<typeof identitySubmitRequestSchema>;
export type IdentitySubmitResponse = z.infer<
  typeof identitySubmitResponseSchema
>;
export type IdentityRejectRequest = z.infer<typeof identityRejectRequestSchema>;
export type AdminIdentityListResponse = z.infer<
  typeof adminIdentityListResponseSchema
>;
export type PhoneVerifyStartRequest = z.infer<
  typeof phoneVerifyStartRequestSchema
>;
export type PhoneVerifyStartResponse = z.infer<
  typeof phoneVerifyStartResponseSchema
>;
export type PhoneVerifyConfirmRequest = z.infer<
  typeof phoneVerifyConfirmRequestSchema
>;
export type PhoneVerifyConfirmResponse = z.infer<
  typeof phoneVerifyConfirmResponseSchema
>;
